import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { SignedAmountInput } from '../components/SignedAmountInput';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import StatusBadge from '../components/StatusBadge';
import WorkflowStepper from '../components/WorkflowStepper';
import { DueDateCell } from '../utils/dueDate';
import { StatusTimeline } from '../components/StatusTimeline';
import { DeleteConfirmDialog } from '../components/DeleteConfirmDialog';
import { AdminBulkActions } from '../components/AdminBulkActions';
import { InvoiceAmountSummary } from '../components/invoice/InvoiceAmountSummary';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import api from '../lib/api';
import { formatRupiah, openPendingPrintWindow, POPUP_BLOCKED_MESSAGE } from '../utils/printHelper';
import { invoiceAmounts } from '../utils/invoiceAmounts';
import { printVoucherInvoice, printVoucherInternal, printVoucherInvoiceGabungan } from '../utils/voucherTemplate';
import { MIN_BATCH, MAX_BATCH, orderMembers, validateVoucherBatch } from '../utils/voucherBatch';
import { findCompany } from '../utils/signatures';
import { Pagination } from '../components/Pagination';
import { Plus, Eye, CheckCircle, XCircle, RefreshCw, CreditCard, FileText, Building2, Send, Trash2, Undo2, Edit, Printer } from 'lucide-react';
import { toast } from 'sonner';


import { EditInternalDetailsDialog } from '../components/payment/EditInternalDetailsDialog';
import { VoucherPrintDialog } from '../components/payment/VoucherPrintDialog';
import { VoucherBatchDialog } from '../components/payment/VoucherBatchDialog';
import { EditDeductionsDialog } from '../components/payment/EditDeductionsDialog';
import { InternalApprovalDialog } from '../components/payment/InternalApprovalDialog';
import { InternalDetailDialog } from '../components/payment/InternalDetailDialog';
import { InvoiceProsesDetailDialog } from '../components/payment/InvoiceProsesDetailDialog';
import { DownPaymentsTable } from '../components/payment/DownPaymentsTable';
import { InternalRequestsTable } from '../components/payment/InternalRequestsTable';
import { InvoiceProsesBayarTable } from '../components/payment/InvoiceProsesBayarTable';
import { bankLabel, scopeAllows, apiErrorMessage } from '../utils/requestTheme';
export default function PaymentRequests() {
  const { user } = useAuth();
  const [internalRequests, setInternalRequests] = useState([]);
  const [opsRequests, setOpsRequests] = useState([]);
  const [downPayments, setDownPayments] = useState([]);
  const [invoicesProsesBayar, setInvoicesProsesBayar] = useState([]);
  const [loading, setLoading] = useState(true);
  // Pagination state per section
  const [invoicePage, setInvoicePage] = useState(1);
  const [invoiceTotalPages, setInvoiceTotalPages] = useState(1);
  const [invoiceTotal, setInvoiceTotal] = useState(0);
  const [internalPage, setInternalPage] = useState(1);
  const [internalTotalPages, setInternalTotalPages] = useState(1);
  const [internalTotal, setInternalTotal] = useState(0);
  const [opsPage, setOpsPage] = useState(1);
  const [opsTotalPages, setOpsTotalPages] = useState(1);
  const [opsTotal, setOpsTotal] = useState(0);
  const [dpPage, setDpPage] = useState(1);
  const [dpTotalPages, setDpTotalPages] = useState(1);
  const [dpTotal, setDpTotal] = useState(0);
  // Refs to preserve current pagination across submit/update actions
  const invoicePageRef = useRef(1);
  const internalPageRef = useRef(1);
  const dpPageRef = useRef(1);
  const opsPageRef = useRef(1);
  const [selectedInternalRequest, setSelectedInternalRequest] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInternalDetail, setShowInternalDetail] = useState(false);
  const [showInvoiceDetail, setShowInvoiceDetail] = useState(false);
  const [showInternalApproval, setShowInternalApproval] = useState(false);
  const [approvalAction, setApprovalAction] = useState('approve');
  const [rejectionReason, setRejectionReason] = useState('');
  // Bank selection state
  const [bankSelections, setBankSelections] = useState({}); // { requestId: 'BSI' | 'BRI' }
  const [processingBank, setProcessingBank] = useState(false);
  // Search filter
  const [searchFilter, setSearchFilter] = useState('');
  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteType, setDeleteType] = useState(''); // internal_payment_request, invoice_full
  // Edit Deductions state (Approval)
  const [showEditDeductions, setShowEditDeductions] = useState(false);
  const [editDeductionsTarget, setEditDeductionsTarget] = useState(null); // {type: 'invoice'|'internal', data: {...}}
  const [editPphType, setEditPphType] = useState('');
  const [editPphPercentage, setEditPphPercentage] = useState(0);
  const [editAdminBank, setEditAdminBank] = useState(0);
  const [editAmount, setEditAmount] = useState(0);
  const [editPembulatan, setEditPembulatan] = useState(0);
  const [editLoading, setEditLoading] = useState(false);
  
  // Voucher print with keterangan
  const [showVoucherDialog, setShowVoucherDialog] = useState(false);
  const [voucherTarget, setVoucherTarget] = useState(null); // {type: 'invoice'|'internal', data: {...}}
  const [voucherKeterangan, setVoucherKeterangan] = useState('');
  // Voucher gabungan — state SENGAJA terpisah dari bankSelections supaya alur
  // "Proses ke Bank" tidak tersentuh sama sekali. Menyimpan objek invoice,
  // bukan hanya id, agar validasi bisa jalan tanpa mencari ulang di array.
  const [voucherBatch, setVoucherBatch] = useState({});
  const [showBatchDialog, setShowBatchDialog] = useState(false);
  const [batchKeterangan, setBatchKeterangan] = useState('');
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  // Edit internal details (tipe_request, keterangan) - admin/approval
  const [showEditDetails, setShowEditDetails] = useState(false);
  const [editDetailsTarget, setEditDetailsTarget] = useState(null);
  const [editDetailsTipeRequest, setEditDetailsTipeRequest] = useState('');
  const [editDetailsKeterangan, setEditDetailsKeterangan] = useState('');
  // Perusahaan (semua) + data penerima (khusus OPR) di dialog edit detail.
  const [editDetailsExtra, setEditDetailsExtra] = useState({});
  const [editDetailsLoading, setEditDetailsLoading] = useState(false);
  const [requestTypes, setRequestTypes] = useState({ ipr: [], opr: [] });
  const [departments, setDepartments] = useState([]);
  // Kata kunci yang benar-benar dikirim ke server. Dipisah dari `searchFilter`
  // (isi kolom) karena halaman ini mencari saat Enter ditekan, bukan otomatis
  // sambil mengetik: satu pencarian menyegarkan empat tabel sekaligus, jadi
  // memicunya per ketikan terasa mengganggu.
  const [appliedSearch, setAppliedSearch] = useState('');
  // `loading` hanya untuk render pertama. Penyegaran berikutnya memakai
  // `refreshing` supaya header dan kolom pencarian tidak pernah di-unmount —
  // meng-unmount-nya membuat fokus hilang di tengah pengetikan.
  const [refreshing, setRefreshing] = useState(false);
  const [companies, setCompanies] = useState([]);
  // Nomor urut request: hasil yang bukan dari permintaan terakhir dibuang,
  // supaya respons lambat untuk kata kunci lama tidak menimpa yang baru.
  const fetchSeqRef = useRef(0);

  const applySearch = (value) => setAppliedSearch(value.trim());

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      applySearch(searchFilter);
    } else if (e.key === 'Escape') {
      setSearchFilter('');
      applySearch('');
    }
  };

  const clearSearch = () => {
    setSearchFilter('');
    applySearch('');
  };

  const fetchData = useCallback(async (iPage, intP, dpP, opsP) => {
    const seq = ++fetchSeqRef.current;
    setRefreshing(true);
    try {
      const searchParam = appliedSearch ? `&search=${encodeURIComponent(appliedSearch)}` : '';
      const isAP = user?.role === 'account_payable';
      const isApproval = user?.role === 'approval' || user?.role === 'admin';
      // Cakupan approval: tabel di luar cakupan tidak diambil (server pasti 403).
      const can = (k) => scopeAllows(user, k);

      const invPage = typeof iPage === 'number' ? iPage : invoicePageRef.current;
      const intPage = typeof intP === 'number' ? intP : internalPageRef.current;
      const dPage = typeof dpP === 'number' ? dpP : dpPageRef.current;
      const oPage = typeof opsP === 'number' ? opsP : opsPageRef.current;

      // Semua permintaan berangkat bersamaan; berurutan membuat latensinya
      // menumpuk sebanyak jumlah tabel di halaman ini.
      const [invoicesRes, internalRes, dpRes, typesRes, opsRes, oprTypesRes] = await Promise.all([
        (isAP || isApproval) && can('invoice')
          ? api.get(`/invoices-full/proses-bayar?page=${invPage}&limit=20${searchParam}`) : null,
        isApproval && can('internal') ? api.get(`/internal/payment-requests?category=internal&page=${intPage}&limit=20${searchParam}`) : null,
        isApproval && can('dp') ? api.get(`/down-payments?page=${dPage}&limit=20${searchParam}`) : null,
        isApproval && can('ipr') ? api.get(`/internal/request-types?category=ipr`).catch(() => null) : null,
        isApproval && can('operasional') ? api.get(`/internal/payment-requests?category=operasional&page=${oPage}&limit=20${searchParam}`) : null,
        isApproval && can('opr') ? api.get(`/internal/request-types?category=opr`).catch(() => null) : null,
      ]);

      // Permintaan yang lebih baru sudah berangkat — buang hasil yang ini.
      if (seq !== fetchSeqRef.current) return;

      if (invoicesRes) {
        const invData = invoicesRes.data;
        setInvoicesProsesBayar(invData.items || []);
        setInvoiceTotalPages(invData.total_pages || 1);
        setInvoiceTotal(invData.total || 0);
        const pg = invData.page || 1;
        setInvoicePage(pg);
        invoicePageRef.current = pg;
      }

      if (internalRes) {
        const intData = internalRes.data;
        setInternalRequests(intData.items || []);
        setInternalTotalPages(intData.total_pages || 1);
        setInternalTotal(intData.total || 0);
        const ip = intData.page || 1;
        setInternalPage(ip);
        internalPageRef.current = ip;
      }

      if (opsRes) {
        const oData = opsRes.data;
        setOpsRequests(oData.items || []);
        setOpsTotalPages(oData.total_pages || 1);
        setOpsTotal(oData.total || 0);
        const op = oData.page || 1;
        setOpsPage(op);
        opsPageRef.current = op;
      }

      if (dpRes) {
        const dpData = dpRes.data;
        setDownPayments(dpData.items || []);
        setDpTotalPages(dpData.total_pages || 1);
        setDpTotal(dpData.total || 0);
        const dp = dpData.page || 1;
        setDpPage(dp);
        dpPageRef.current = dp;
      }

      if (typesRes || oprTypesRes) {
        setRequestTypes({ ipr: typesRes?.data || [], opr: oprTypesRes?.data || [] });
      }
    } catch (error) {
      if (seq !== fetchSeqRef.current) return;
      console.error('Failed to fetch data:', error);
      toast.error('Gagal memuat data');
    } finally {
      if (seq === fetchSeqRef.current) {
        setRefreshing(false);
        setLoading(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, user?.approval_scope, appliedSearch]);

  useEffect(() => {
    // Initial + search-change fetch. Explicitly go to page 1 for all three tables.
    fetchData(1, 1, 1, 1);
  }, [fetchData]);

  // Pilihan voucher gabungan dibersihkan saat pencarian berubah maupun saat
  // pindah halaman. Konsekuensinya penting: setiap invoice terpilih selalu
  // berada di array yang sedang dirender, sehingga jeda antara memilih dan
  // mencetak sama persis dengan tombol cetak satuan yang sudah ada — tidak
  // ada kelas masalah data basi baru. Harganya: untuk menggabungkan, user
  // mencari nama supplier dulu agar invoice-nya berkumpul di satu halaman.
  useEffect(() => {
    setVoucherBatch((prev) => (Object.keys(prev).length ? {} : prev));
  }, [appliedSearch, invoicePage]);

  // Master perusahaan untuk nama penanda tangan di voucher — dibaca dari master,
  // bukan salinan beku di dokumen.
  useEffect(() => {
    api.get('/companies')
      .then((res) => setCompanies(res.data || []))
      .catch(() => { /* voucher tetap tercetak, kolomnya saja kosong */ });
    if (user?.role === 'approval' || user?.role === 'admin') {
      api.get('/departments').then((res) => setDepartments(res.data || [])).catch(() => {});
    }
  }, [user?.role]);

  const handleInternalApproval = async (requestId) => {
    try {
      await api.patch(`/internal/payment-requests/${requestId}/approve`, {
        action: approvalAction,
        rejection_reason: approvalAction === 'reject' ? rejectionReason : null
      });
      toast.success(`Payment request ${approvalAction === 'approve' ? 'disetujui' : 'ditolak'}!`);
      setShowInternalApproval(false);
      setRejectionReason('');
      fetchData();
    } catch (error) {
      toast.error('Gagal memproses approval');
    }
  };

  // Bank selection handlers
  const handleBankSelect = (requestId, requestType, bankType) => {
    const key = `${requestType}_${requestId}`;
    setBankSelections(prev => {
      const newSelections = { ...prev };
      if (newSelections[key] === bankType) {
        delete newSelections[key]; // Unselect if same bank clicked
      } else {
        newSelections[key] = bankType;
      }
      return newSelections;
    });
  };

  // Kunci seleksi: `internal_<id>` (IPR & OPR) / `dp_<id>`. `onlyType` membatasi
  // ke satu jenis supaya tombol Proses di kartu Internal tidak ikut mengirim
  // DP yang dicentang di kartu Down Payment, dan sebaliknya.
  const getSelectedForBank = (bankType, onlyType = null) => {
    return Object.entries(bankSelections)
      .filter(([key, bank]) => bank === bankType && (!onlyType || key.startsWith(`${onlyType}_`)))
      .map(([key]) => {
        // Handle dp_<id>, internal_<id>, invoice_<id>
        const firstUnderscore = key.indexOf('_');
        const type = key.substring(0, firstUnderscore);
        const id = key.substring(firstUnderscore + 1);
        return { request_id: id, request_type: type === 'dp' ? 'down_payment' : type, bank_type: bankType };
      });
  };

  const handleProcessToBank = async (bankType = null, onlyType = null) => {
    // If bankType is provided, use the old format (internal/OPR/DP requests)
    // If not, process all selected invoices (new format)
    if (bankType) {
      const selections = getSelectedForBank(bankType, onlyType);
      if (selections.length === 0) {
        toast.error(`Pilih transaksi untuk ${bankLabel(bankType)} terlebih dahulu`);
        return;
      }

      setProcessingBank(true);
      try {
        await api.post(`/bank-process/select`, selections);
        toast.success(`${selections.length} transaksi berhasil dikirim ke Proses Bank ${bankLabel(bankType)}`);
        // Clear selections for this bank
        setBankSelections(prev => {
          const newSelections = { ...prev };
          Object.keys(newSelections).forEach(key => {
            if (newSelections[key] === bankType && (!onlyType || key.startsWith(`${onlyType}_`))) delete newSelections[key];
          });
          return newSelections;
        });
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Gagal memproses ke bank');
      } finally {
        setProcessingBank(false);
      }
    } else {
      // New format: process all selected invoices to bank
      const selectedInvoices = Object.entries(bankSelections)
        .filter(([id, bank]) => bank && !id.includes('_')) // invoiceId -> bank
        .map(([invoiceId, bank]) => ({
          invoice_id: invoiceId,
          bank_type: bank
        }));

      if (selectedInvoices.length === 0) {
        toast.error('Pilih invoice dan bank terlebih dahulu');
        return;
      }

      setProcessingBank(true);
      try {
        await api.post(`/payment-requests/process-to-bank`, { invoices: selectedInvoices });
        toast.success(`${selectedInvoices.length} invoice berhasil dikirim ke Proses Bank`);
        // Hanya seleksi invoice yang dibersihkan; centang Internal/OPR/DP tetap.
        setBankSelections(prev => Object.fromEntries(Object.entries(prev).filter(([k]) => k.includes('_'))));
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Gagal memproses ke bank');
      } finally {
        setProcessingBank(false);
      }
    }
  };

  // Admin delete functions
  const openDeleteDialog = (item, type) => {
    setItemToDelete(item);
    setDeleteType(type);
    setDeleteDialogOpen(true);
  };

  // Voucher printing lives in utils/voucherTemplate.js so AP, Approval and the
  // Invoices page all print exactly the same numbers.
  const printVoucherPembayaran = (invoice, keteranganTambahan = '') =>
    printVoucherInvoice(invoice, keteranganTambahan, {
      company: findCompany(companies, invoice.company_id),
      printedBy: (user?.name || '').toUpperCase(),
    });

  // ---- Voucher gabungan ----
  const canBatchVoucher = ['account_payable', 'approval', 'admin'].includes(user?.role);
  const batchMembers = Object.values(voucherBatch);
  const batchCheck = useMemo(() => validateVoucherBatch(batchMembers), [voucherBatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleBatchMember = (invoice, checked) => {
    setVoucherBatch((prev) => {
      const next = { ...prev };
      if (checked) next[invoice.id] = invoice;
      else delete next[invoice.id];
      return next;
    });
  };

  const clearBatch = () => setVoucherBatch({});

  const handlePrintBatchVoucher = async () => {
    if (batchMembers.length < MIN_BATCH || !batchCheck.ok) return;

    // Jendela dibuka SINKRON di dalam handler klik. window.open() setelah
    // await diblokir Safari & Firefox default karena sudah keluar dari
    // call-stack gesture user. Urutannya tetap stempel-dulu-cetak-kemudian;
    // jendelanya saja yang disiapkan lebih awal.
    const printWindow = openPendingPrintWindow('Menyiapkan voucher gabungan…');
    if (!printWindow) {
      toast.error(POPUP_BLOCKED_MESSAGE);
      return;
    }

    setBatchSubmitting(true);
    const ordered = orderMembers(batchMembers);
    let group;
    try {
      const res = await api.post('/invoices-full/voucher-gabungan', {
        invoice_ids: ordered.map((m) => m.id),
      });
      group = res.data;
    } catch (error) {
      // Stempel gagal => tidak ada kertas. Jendela ditutup supaya user tidak
      // melihat tab kosong tanpa penjelasan.
      printWindow.close();
      const detail = error.response?.data?.detail;
      toast.error(
        (typeof detail === 'string' ? detail : detail?.message)
        || 'Gagal membuat voucher gabungan'
      );
      setBatchSubmitting(false);
      return;
    }

    // Stempel SUDAH tercatat di server. Kegagalan setelah titik ini hanya
    // soal cetak, jadi pesannya berbeda: cukup cetak ulang dengan tombol yang
    // sama (server mengenali grup yang sama sebagai cetak ulang).
    try {
      const head = ordered[0];
      if (printWindow.closed) {
        throw new Error('WINDOW_CLOSED');
      }
      printVoucherInvoiceGabungan(printWindow, ordered, batchKeterangan, {
        company: findCompany(companies, head.company_id),
        printedBy: (user?.name || '').toUpperCase(),
      }, group);

      toast.success(
        group.reprint
          ? `Voucher gabungan dicetak ulang (cetakan ke-${group.print_count}) — ${group.voucher_gabungan_label}`
          : `Voucher gabungan ${group.voucher_gabungan_label} dibuat untuk ${group.count} invoice`
      );
      setShowBatchDialog(false);
      setBatchKeterangan('');
      clearBatch();
    } catch (error) {
      try { printWindow.close(); } catch (e) { /* sudah tertutup */ }
      toast.error(
        `Voucher gabungan ${group?.voucher_gabungan_label || ''} sudah tercatat, tetapi gagal dicetak`
        + (error?.message === 'WINDOW_CLOSED' ? ' (jendela cetak sudah ditutup)' : '')
        + '. Klik Print Voucher Gabungan lagi untuk cetak ulang.'
      );
    } finally {
      setBatchSubmitting(false);
      fetchData();
    }
  };

  const handleDelete = async (reason) => {
    if (!itemToDelete || !deleteType) return;
    if (user?.role !== 'admin' && !(reason || '').trim()) {
      toast.error('Alasan penghapusan wajib diisi');
      return;
    }
    
    setDeleteLoading(true);
    try {
      await api.delete(`/admin/transactions/${deleteType}/${itemToDelete.id}`, {
        data: { reason }
      });
      const itemNumber = itemToDelete.request_number || itemToDelete.invoice_number || itemToDelete.dp_number;
      toast.success(`${itemNumber} berhasil dihapus`);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      setDeleteType('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus data');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Revert invoice to previous status
  const handleRevertInvoice = async (invoiceId) => {
    try {
      const res = await api.patch(`/invoices-full/${invoiceId}/revert`, { reason: '' });
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengembalikan invoice');
    }
  };

  // Revert internal payment request
  const handleRevertInternal = async (requestId) => {
    try {
      const res = await api.patch(`/internal/payment-requests/${requestId}/revert`, { reason: '' });
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengembalikan request');
    }
  };

  // Open edit deductions dialog
  const openEditDeductions = (type, data) => {
    setEditDeductionsTarget({ type, data });
    setEditPphType(data.pph_type || '');
    setEditPphPercentage(data.pph_percentage || 0);
    setEditAdminBank(data.admin_bank || 0);
    setEditAmount(data.amount || data.total_amount || 0);
    setEditPembulatan(data.pembulatan || 0);
    setShowEditDeductions(true);
  };

  // Save edited deductions
  const handleSaveDeductions = async () => {
    if (!editDeductionsTarget) return;
    setEditLoading(true);
    try {
      const endpoint = editDeductionsTarget.type === 'invoice'
        ? `/invoices-full/${editDeductionsTarget.data.id}/edit-deductions`
        : `/internal/payment-requests/${editDeductionsTarget.data.id}/edit-deductions`;
      
      const payload = {
        pph_type: editPphType || null,
        pph_percentage: editPphPercentage || 0,
        admin_bank: editAdminBank || 0
      };
      if (editDeductionsTarget.type === 'internal') {
        payload.amount = editAmount || 0;
        payload.pph_amount = (editPphType && editPphPercentage > 0) ? (editAmount || 0) * (editPphPercentage / 100) : 0;
      }
      if (editDeductionsTarget.type === 'invoice') {
        payload.pembulatan = editPembulatan || 0;
      }
      await api.patch(endpoint, payload);
      toast.success('Data berhasil diperbarui');
      setShowEditDeductions(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memperbarui');
    } finally {
      setEditLoading(false);
    }
  };

  const openEditDetails = (request) => {
    setEditDetailsTarget(request);
    setEditDetailsTipeRequest(request.tipe_request || '');
    setEditDetailsKeterangan(request.keterangan || '');
    setEditDetailsExtra({
      company_id: request.company_id || '',
      departemen_id: request.departemen_id || '',
      penerima_nama: request.penerima_nama || '',
      penerima_rekening: request.penerima_rekening || '',
      penerima_bank: request.penerima_bank || '',
      penerima_wa: request.penerima_wa || '',
      penerima_email: request.penerima_email || '',
    });
    setShowEditDetails(true);
  };

  // Handle save edited request details (tipe, keterangan, perusahaan, penerima OPR)
  const handleSaveEditDetails = async () => {
    if (!editDetailsTarget) return;
    const t = editDetailsTarget;
    const x = editDetailsExtra;
    const payload = { tipe_request: editDetailsTipeRequest, keterangan: editDetailsKeterangan };
    // Hanya kirim field yang berubah: server menolak perubahan perusahaan/
    // penerima setelah request masuk Proses Bank.
    if (x.company_id && x.company_id !== (t.company_id || '')) payload.company_id = x.company_id;
    if (x.departemen_id && x.departemen_id !== (t.departemen_id || '')) payload.departemen_id = x.departemen_id;
    if (t.category === 'operasional') {
      if (!x.penerima_nama?.trim()) return toast.error('Nama penerima wajib diisi');
      if (!x.penerima_rekening) return toast.error('Nomor rekening wajib diisi');
      if (!x.penerima_bank) return toast.error('Nama bank wajib dipilih');
      ['penerima_nama', 'penerima_rekening', 'penerima_bank', 'penerima_wa', 'penerima_email'].forEach((f) => {
        if ((x[f] || '') !== (t[f] || '')) payload[f] = x[f] || '';
      });
    }
    setEditDetailsLoading(true);
    try {
      await api.patch(`/internal/payment-requests/${t.id}/edit-details`, payload);
      toast.success('Detail berhasil diperbarui');
      setShowEditDetails(false);
      fetchData();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Gagal memperbarui detail'));
    } finally {
      setEditDetailsLoading(false);
    }
  };

  // Calculate invoice age (umur hutang) in days
  const getUmurHutang = (invoiceDate) => {
    if (!invoiceDate) return 0;
    const invDate = new Date(invoiceDate);
    const now = new Date();
    const diffTime = now - invDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Get umur hutang badge color
  const getUmurBadge = (days) => {
    if (days <= 7) {
      return { label: `${days} hari`, color: 'bg-green-100 text-green-700', status: 'DALAM BATAS' };
    } else if (days <= 14) {
      return { label: `${days} hari`, color: 'bg-amber-100 text-amber-700', status: 'PEMANTAUAN' };
    } else {
      return { label: `${days} hari`, color: 'bg-red-100 text-red-700 font-bold', status: 'LEWAT BATAS' };
    }
  };

  // Server-side search (debounced) - results cover all pages, not just current page
  const filteredInvoicesProsesBayar = invoicesProsesBayar;
  const filteredInternalRequests = internalRequests;
  const filteredDownPayments = downPayments;

  // Hanya render pertama yang menampilkan spinner layar penuh. Penyegaran
  // karena pencarian tidak boleh sampai ke sini: mengganti seluruh halaman
  // akan ikut membuang kolom pencarian beserta fokus kursornya.
  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="payment-requests-page">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Payment Requests</h1>
          <div className="flex gap-2 items-center">
            {['account_payable', 'approval', 'admin'].includes(user?.role) && (
              <div className="relative">
                <Input
                  placeholder="Cari lalu tekan Enter..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  className="w-72 h-9 pr-16"
                  data-testid="payment-search-filter"
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                  {refreshing && (
                    <span
                      className="h-4 w-4 rounded-full border-2 border-slate-300 border-b-blue-600 animate-spin"
                      data-testid="payment-search-busy"
                    />
                  )}
                  {searchFilter && !refreshing && (
                    <button
                      type="button"
                      onClick={clearSearch}
                      className="text-slate-400 hover:text-slate-700 text-sm leading-none px-1"
                      title="Hapus pencarian (Esc)"
                      data-testid="payment-search-clear"
                    >
                      ✕
                    </button>
                  )}
                  {/* Penanda kata kunci yang sedang aktif — tanpa ini, isi kolom
                      yang belum di-Enter bisa disangka sudah tersaring. */}
                  {appliedSearch && (
                    <span
                      className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-semibold"
                      title={`Hasil disaring: "${appliedSearch}"`}
                      data-testid="payment-search-active"
                    >
                      aktif
                    </span>
                  )}
                </div>
              </div>
            )}
            {/* Tanpa pembungkus arrow, event klik ikut terkirim sebagai argumen halaman. */}
            <Button variant="outline" onClick={() => fetchData()} data-testid="refresh-button">
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </Button>
          </div>
        </div>

        {scopeAllows(user, 'invoice') && <InvoiceProsesBayarTable appliedSearch={appliedSearch} bankSelections={bankSelections} batchCheck={batchCheck} batchMembers={batchMembers} canBatchVoucher={canBatchVoucher} clearBatch={clearBatch} fetchData={fetchData} filteredInvoicesProsesBayar={filteredInvoicesProsesBayar} getUmurBadge={getUmurBadge} getUmurHutang={getUmurHutang} handleProcessToBank={handleProcessToBank} handleRevertInvoice={handleRevertInvoice} invoicePage={invoicePage} invoiceTotal={invoiceTotal} invoiceTotalPages={invoiceTotalPages} openDeleteDialog={openDeleteDialog} openEditDeductions={openEditDeductions} processingBank={processingBank} setBankSelections={setBankSelections} setBatchKeterangan={setBatchKeterangan} setSelectedInvoice={setSelectedInvoice} setShowBatchDialog={setShowBatchDialog} setShowInvoiceDetail={setShowInvoiceDetail} setShowVoucherDialog={setShowVoucherDialog} setVoucherKeterangan={setVoucherKeterangan} setVoucherTarget={setVoucherTarget} toggleBatchMember={toggleBatchMember} user={user} voucherBatch={voucherBatch} />}

        {scopeAllows(user, 'internal') && <InternalRequestsTable variant="internal" appliedSearch={appliedSearch} bankSelections={bankSelections} fetchData={fetchData} filteredInternalRequests={filteredInternalRequests} getSelectedForBank={getSelectedForBank} handleBankSelect={handleBankSelect} handleProcessToBank={handleProcessToBank} handleRevertInternal={handleRevertInternal} internalPage={internalPage} internalTotal={internalTotal} internalTotalPages={internalTotalPages} onPageChange={(p) => fetchData(undefined, p, undefined, undefined)} openDeleteDialog={openDeleteDialog} openEditDeductions={openEditDeductions} openEditDetails={openEditDetails} processingBank={processingBank} setSelectedInternalRequest={setSelectedInternalRequest} setShowInternalDetail={setShowInternalDetail} setShowVoucherDialog={setShowVoucherDialog} setVoucherKeterangan={setVoucherKeterangan} setVoucherTarget={setVoucherTarget} user={user} />}

        {scopeAllows(user, 'operasional') && <InternalRequestsTable variant="operasional" appliedSearch={appliedSearch} bankSelections={bankSelections} fetchData={fetchData} filteredInternalRequests={opsRequests} getSelectedForBank={getSelectedForBank} handleBankSelect={handleBankSelect} handleProcessToBank={handleProcessToBank} handleRevertInternal={handleRevertInternal} internalPage={opsPage} internalTotal={opsTotal} internalTotalPages={opsTotalPages} onPageChange={(p) => fetchData(undefined, undefined, undefined, p)} openDeleteDialog={openDeleteDialog} openEditDeductions={openEditDeductions} openEditDetails={openEditDetails} processingBank={processingBank} setSelectedInternalRequest={setSelectedInternalRequest} setShowInternalDetail={setShowInternalDetail} setShowVoucherDialog={setShowVoucherDialog} setVoucherKeterangan={setVoucherKeterangan} setVoucherTarget={setVoucherTarget} user={user} />}

        {scopeAllows(user, 'dp') && <DownPaymentsTable appliedSearch={appliedSearch} bankSelections={bankSelections} dpPage={dpPage} dpTotal={dpTotal} dpTotalPages={dpTotalPages} fetchData={fetchData} filteredDownPayments={filteredDownPayments} getSelectedForBank={getSelectedForBank} handleBankSelect={handleBankSelect} handleProcessToBank={handleProcessToBank} openDeleteDialog={openDeleteDialog} processingBank={processingBank} setShowVoucherDialog={setShowVoucherDialog} setVoucherKeterangan={setVoucherKeterangan} setVoucherTarget={setVoucherTarget} user={user} />}

        <InvoiceProsesDetailDialog selectedInvoice={selectedInvoice} setShowInvoiceDetail={setShowInvoiceDetail} showInvoiceDetail={showInvoiceDetail} />


        <InternalDetailDialog selectedInternalRequest={selectedInternalRequest} setShowInternalDetail={setShowInternalDetail} showInternalDetail={showInternalDetail} user={user} />

        <InternalApprovalDialog approvalAction={approvalAction} handleInternalApproval={handleInternalApproval} rejectionReason={rejectionReason} selectedInternalRequest={selectedInternalRequest} setRejectionReason={setRejectionReason} setShowInternalApproval={setShowInternalApproval} showInternalApproval={showInternalApproval} />

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onConfirm={handleDelete}
          requireReason={user?.role !== 'admin'}
          title="Hapus Transaksi"
          description="Apakah Anda yakin ingin menghapus data ini? Tindakan ini tidak dapat dibatalkan dan akan dicatat dalam log penghapusan."
          itemName={itemToDelete ? `${itemToDelete.request_number || itemToDelete.invoice_number || itemToDelete.dp_number} - Rp ${(itemToDelete.nilai_pengajuan || itemToDelete.total_amount || itemToDelete.amount || 0).toLocaleString('id-ID')}` : ''}
          loading={deleteLoading}
        />

        <EditDeductionsDialog editAdminBank={editAdminBank} editAmount={editAmount} editDeductionsTarget={editDeductionsTarget} editLoading={editLoading} editPembulatan={editPembulatan} editPphPercentage={editPphPercentage} editPphType={editPphType} handleSaveDeductions={handleSaveDeductions} setEditAdminBank={setEditAdminBank} setEditAmount={setEditAmount} setEditPembulatan={setEditPembulatan} setEditPphPercentage={setEditPphPercentage} setEditPphType={setEditPphType} setShowEditDeductions={setShowEditDeductions} showEditDeductions={showEditDeductions} />

        {/* Dialog voucher GABUNGAN — sengaja terpisah dari dialog voucher
            satuan di bawah, yang mem-dereference voucherTarget.data secara
            tunggal dan akan rusak kalau diberi payload berisi array. */}
        <VoucherBatchDialog batchCheck={batchCheck} batchKeterangan={batchKeterangan} batchMembers={batchMembers} batchSubmitting={batchSubmitting} handlePrintBatchVoucher={handlePrintBatchVoucher} setBatchKeterangan={setBatchKeterangan} setShowBatchDialog={setShowBatchDialog} showBatchDialog={showBatchDialog} />

        <VoucherPrintDialog companies={companies} printedBy={(user?.name || '').toUpperCase()} printVoucherPembayaran={printVoucherPembayaran} setShowVoucherDialog={setShowVoucherDialog} setVoucherKeterangan={setVoucherKeterangan} showVoucherDialog={showVoucherDialog} voucherKeterangan={voucherKeterangan} voucherTarget={voucherTarget} />

        <EditInternalDetailsDialog companies={companies} departments={departments} editDetailsExtra={editDetailsExtra} setEditDetailsExtra={setEditDetailsExtra} editDetailsKeterangan={editDetailsKeterangan} editDetailsLoading={editDetailsLoading} editDetailsTarget={editDetailsTarget} editDetailsTipeRequest={editDetailsTipeRequest} handleSaveEditDetails={handleSaveEditDetails} requestTypes={requestTypes} setEditDetailsKeterangan={setEditDetailsKeterangan} setEditDetailsTipeRequest={setEditDetailsTipeRequest} setShowEditDetails={setShowEditDetails} showEditDetails={showEditDetails} />
      </div>
    </Layout>
  );
}


